import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Users, RefreshCw, KeyRound, Mail, Shield, Loader2, ClipboardCopy, X,
  UserPlus, Search, ScrollText, Send, XCircle, Ban, CheckCircle2, Trash2,
  ChevronRight, ChevronDown,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/gov`;

const ROLES = [
  { v: 'purchaser', l: 'Purchaser' },
  { v: 'approver', l: 'Approver' },
  { v: 'finance_officer', l: 'Finance Officer' },
  { v: 'division_admin', l: 'Division Administrator' },
  { v: 'agency_admin', l: 'Agency Administrator' },
];

const ROLE_BADGE = {
  super_admin: 'bg-rose-100 text-rose-800',
  admin: 'bg-rose-100 text-rose-800',
  agency_admin: 'bg-indigo-100 text-indigo-800',
  purchaser: 'bg-blue-100 text-blue-800',
  approver: 'bg-amber-100 text-amber-800',
  finance_officer: 'bg-emerald-100 text-emerald-800',
};

const INV_STATUS_STYLE = {
  invitation_sent: 'bg-blue-100 text-blue-800 border-blue-200',
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  expired: 'bg-orange-100 text-orange-800 border-orange-200',
  cancelled: 'bg-slate-100 text-slate-700 border-slate-200',
  accepted: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString() : '—';

const GovAgencyUsers = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('users');
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', role: 'purchaser', phone: '', division_id: '', department_id: '' });
  const [inviteDivisions, setInviteDivisions] = useState([]);
  const [inviteDepartments, setInviteDepartments] = useState([]);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetResult, setResetResult] = useState(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [auditFor, setAuditFor] = useState(null);
  const [auditRows, setAuditRows] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [u, i] = await Promise.all([
        axios.get(`${API}/agency-users`),
        axios.get(`${API}/agency-users/invitations`).catch(() => ({ data: [] })),
      ]);
      setUsers(u.data || []);
      setInvitations(i.data || []);
    } catch (e) {
      toast({ title: 'Load failed', description: e.response?.data?.detail || 'Server error', variant: 'destructive' });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const isUskeAdmin = me?.role === 'super_admin' || me?.role === 'admin';

  const visibleUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter(u => {
      if (!q) return true;
      return [u.name, u.email, u.role, u.department].filter(Boolean).some(v => String(v).toLowerCase().includes(q));
    });
  }, [users, search]);

  const visibleInvites = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invitations.filter(i => {
      if (!q) return true;
      return [i.invitation_ref, i.user_name, i.user_email, i.role].filter(Boolean).some(v => String(v).toLowerCase().includes(q));
    });
  }, [invitations, search]);

  const counts = {
    users: users.filter(u => !u.pending_activation).length,
    pending: invitations.filter(i => i.status === 'invitation_sent' || i.status === 'pending').length,
    expired: invitations.filter(i => i.status === 'expired').length,
  };

  // Load Divisions when the invite dialog opens
  useEffect(() => {
    if (!inviteOpen) return;
    const agencyId = me?.agency_id;
    if (!agencyId) return;
    axios.get(`${API}/agencies/${agencyId}/divisions`)
      .then((r) => setInviteDivisions(r.data || []))
      .catch(() => setInviteDivisions([]));
  }, [inviteOpen, me]);

  // Reload Departments whenever the chosen Division changes
  useEffect(() => {
    if (!inviteForm.division_id) { setInviteDepartments([]); return; }
    axios.get(`${API}/divisions/${inviteForm.division_id}/departments`)
      .then((r) => setInviteDepartments(r.data || []))
      .catch(() => setInviteDepartments([]));
  }, [inviteForm.division_id]);

  // -------- Invite --------
  const submitInvite = async (e) => {
    e.preventDefault();
    setInviteSubmitting(true);
    try {
      // Strip empty division/department so backend treats them as unset
      const payload = { ...inviteForm };
      if (!payload.division_id) delete payload.division_id;
      if (!payload.department_id) delete payload.department_id;
      const res = await axios.post(`${API}/agency-users/invite`, payload);
      setInviteResult(res.data);
      toast({ title: 'Invitation sent', description: `${inviteForm.email} · ${res.data.invitation_ref}` });
      await fetchAll();
    } catch (err) {
      toast({ title: 'Invite failed', description: err.response?.data?.detail || 'Server error', variant: 'destructive' });
    } finally { setInviteSubmitting(false); }
  };
  const closeInvite = () => { setInviteOpen(false); setInviteForm({ name: '', email: '', role: 'purchaser', phone: '', division_id: '', department_id: '' }); setInviteDepartments([]); setInviteResult(null); };

  // -------- User row actions --------
  const runAction = async (label, fn) => {
    try { await fn(); toast({ title: label }); await fetchAll(); }
    catch (e) { toast({ title: `${label} failed`, description: e.response?.data?.detail || '', variant: 'destructive' }); }
  };
  const disableUser  = (u) => window.confirm(`Disable ${u.email}?`) && runAction('User disabled',  () => axios.post(`${API}/agency-users/${u.id}/disable`, {}));
  const enableUser   = (u) => runAction('User re-enabled', () => axios.post(`${API}/agency-users/${u.id}/enable`, {}));
  const deleteUser   = (u) => window.confirm(`Permanently delete ${u.email}? This cannot be undone.`) && runAction('User deleted',  () => axios.delete(`${API}/agency-users/${u.id}`));
  const changeRole   = async (u, newRole) => runAction(`Role changed to ${newRole}`, () => axios.post(`${API}/agency-users/${u.id}/role`, { role: newRole }));
  const toggleViewOnly = async (u, val) => runAction(val ? 'View-only enabled' : 'View-only disabled', () => axios.post(`${API}/agency-users/${u.id}/view-only`, { view_only: !!val }));

  const resendInvite = (i) => runAction('Invitation resent', () => axios.post(`${API}/agency-users/invitations/${i.id}/resend`, {}));
  const cancelInvite = (i) => window.confirm(`Cancel invitation ${i.invitation_ref}?`) && runAction('Invitation cancelled', () => axios.post(`${API}/agency-users/invitations/${i.id}/cancel`, {}));

  const openReset = (u) => { setResetTarget(u); setResetPwd(''); setResetResult(null); };
  const closeReset = () => { setResetTarget(null); setResetPwd(''); setResetResult(null); };
  const submitReset = async (e) => {
    e.preventDefault();
    setResetSubmitting(true);
    try {
      const res = await axios.post(`${API}/agency-users/${resetTarget.id}/reset-password`, { new_password: resetPwd || undefined });
      setResetResult({ ...res.data, used_password: resetPwd || '(auto-generated)' });
      toast({ title: 'Password reset', description: `Email sent to ${res.data.user_email}` });
      await fetchAll();
    } catch (err) {
      toast({ title: 'Reset failed', description: err.response?.data?.detail || 'Server error', variant: 'destructive' });
    } finally { setResetSubmitting(false); }
  };

  const openAudit = async (u) => {
    setAuditFor(u); setAuditRows([]);
    try {
      const res = await axios.get(`${API}/agency-users/${u.id}/audit`);
      setAuditRows(res.data || []);
    } catch (e) { toast({ title: 'Audit fetch failed', variant: 'destructive' }); }
  };

  return (
    <div className="space-y-4" data-testid="gov-agency-users-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="h-7 w-7 text-blue-700" /> {isUskeAdmin ? 'All Portal Users' : 'Organisation Users'}
          </h1>
          <p className="text-sm text-slate-500">Invite, activate, disable, or delete users. Every action is logged for compliance.</p>
        </div>
        <div className="flex gap-2 items-center">
          <div className="relative">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-7 w-56" data-testid="user-search" />
          </div>
          <Button variant="outline" onClick={fetchAll} data-testid="users-refresh"><RefreshCw className="h-4 w-4" /></Button>
          <Button onClick={() => setInviteOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="invite-user-btn">
            <UserPlus className="h-4 w-4 mr-1" /> Invite User
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-2">
        <div className="p-3 rounded-lg border-2 border-slate-200 bg-white"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Active Users</div><div className="text-2xl font-bold text-slate-800">{counts.users}</div></div>
        <div className="p-3 rounded-lg border-2 border-blue-200 bg-blue-50"><div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">Invites Pending</div><div className="text-2xl font-bold text-blue-900">{counts.pending}</div></div>
        <div className="p-3 rounded-lg border-2 border-orange-200 bg-orange-50"><div className="text-[10px] uppercase tracking-wider text-orange-700 font-semibold">Invites Expired</div><div className="text-2xl font-bold text-orange-900">{counts.expired}</div></div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="users" data-testid="tab-users">Users ({users.length})</TabsTrigger>
          <TabsTrigger value="invitations" data-testid="tab-invitations">Invitations ({invitations.length})</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-10 w-10 animate-spin text-blue-700" /></div>
      ) : tab === 'users' ? (
        <Card className="border-2">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="text-left p-3">User</th>
                    <th className="text-left p-3">Role</th>
                    <th className="text-left p-3">View Only</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">MFA</th>
                    <th className="text-left p-3">Last Login</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleUsers.length === 0 ? (
                    <tr><td colSpan={7} className="p-6 text-center text-slate-400">No users match.</td></tr>
                  ) : visibleUsers.map(u => (
                    <tr key={u.id} className="border-t hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                      <td className="p-3">
                        <div className="font-medium text-slate-800">{u.name || '—'}</div>
                        <a className="text-xs text-blue-700 underline" href={`mailto:${u.email}`}>{u.email}</a>
                        {u.department && <div className="text-[10px] text-slate-400">{u.department}</div>}
                      </td>
                      <td className="p-3">
                        {u.email !== me?.email && !u.pending_activation && !(u.role === 'super_admin' || u.role === 'admin') ? (
                          <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                            <SelectTrigger className="h-7 w-36 text-xs" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                          </Select>
                        ) : (
                          <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${ROLE_BADGE[u.role] || 'bg-slate-100 text-slate-600'}`}>{(u.role || '').replace('_', ' ')}</span>
                        )}
                      </td>
                      <td className="p-3">
                        {u.email !== me?.email && !(u.role === 'super_admin' || u.role === 'admin') ? (
                          <label className="inline-flex items-center gap-1 cursor-pointer text-xs">
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-blue-700"
                              checked={!!u.view_only}
                              onChange={(e) => toggleViewOnly(u, e.target.checked)}
                              data-testid={`view-only-${u.id}`}
                            />
                            <span className="text-slate-600">{u.view_only ? 'On' : 'Off'}</span>
                          </label>
                        ) : (
                          <span className="text-[10px] text-slate-400">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        {u.pending_activation ? <span className="text-blue-700 text-xs">● Pending</span>
                        : u.is_active ? <span className="text-emerald-700 text-xs">● Active</span>
                        : <span className="text-slate-400 text-xs">● Disabled</span>}
                        {u.must_change_password && <div className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded inline-block mt-1">⚠ Must change PW</div>}
                      </td>
                      <td className="p-3 text-xs">{u.mfa_enabled ? <span className="text-emerald-700"><Shield className="inline h-3 w-3" /> On</span> : <span className="text-slate-400">Off</span>}</td>
                      <td className="p-3 text-xs text-slate-500">{fmtDate(u.last_login_at || u.activated_at || u.created_at)}</td>
                      <td className="p-3 text-right">
                        {u.email !== me?.email ? (
                          <div className="inline-flex items-center gap-1">
                            <Button size="sm" variant="outline" onClick={() => openReset(u)} title="Reset password" data-testid={`reset-${u.id}`}>
                              <KeyRound className="h-3.5 w-3.5" />
                            </Button>
                            {u.is_active
                              ? <Button size="sm" variant="outline" className="border-orange-300 text-orange-700" onClick={() => disableUser(u)} title="Disable" data-testid={`disable-${u.id}`}><Ban className="h-3.5 w-3.5" /></Button>
                              : <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700" onClick={() => enableUser(u)} title="Enable" data-testid={`enable-${u.id}`}><CheckCircle2 className="h-3.5 w-3.5" /></Button>}
                            <Button size="sm" variant="ghost" onClick={() => openAudit(u)} title="Audit" data-testid={`audit-user-${u.id}`}><ScrollText className="h-3.5 w-3.5" /></Button>
                            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => deleteUser(u)} title="Delete" data-testid={`delete-user-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        ) : <span className="text-[10px] text-slate-400">(you)</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-2">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="text-left p-3">Invitation ID</th>
                    <th className="text-left p-3">User</th>
                    <th className="text-left p-3">Role</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Sent</th>
                    <th className="text-left p-3">Expires</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleInvites.length === 0 ? (
                    <tr><td colSpan={7} className="p-6 text-center text-slate-400">No invitations yet.</td></tr>
                  ) : visibleInvites.map(i => (
                    <tr key={i.id} className="border-t hover:bg-slate-50" data-testid={`inv-row-${i.id}`}>
                      <td className="p-3"><code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded border">{i.invitation_ref}</code></td>
                      <td className="p-3">
                        <div className="font-medium">{i.user_name}</div>
                        <div className="text-xs text-slate-500">{i.user_email}</div>
                      </td>
                      <td className="p-3"><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${ROLE_BADGE[i.role] || 'bg-slate-100 text-slate-600'}`}>{(i.role || '').replace('_', ' ')}</span></td>
                      <td className="p-3"><span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded border ${INV_STATUS_STYLE[i.status] || 'bg-slate-100'}`}>{i.status.replace('_', ' ')}</span>
                        {i.resend_count > 0 && <div className="text-[10px] text-slate-400 mt-1">resent {i.resend_count}×</div>}
                      </td>
                      <td className="p-3 text-xs text-slate-500">{fmtDate(i.sent_at)}<div className="text-[10px] text-slate-400">by {i.invited_by_email}</div></td>
                      <td className="p-3 text-xs text-slate-500">{fmtDate(i.expires_at)}</td>
                      <td className="p-3 text-right">
                        <div className="inline-flex gap-1">
                          {(i.status === 'invitation_sent' || i.status === 'expired' || i.status === 'pending') && (
                            <>
                              <Button size="sm" variant="outline" onClick={() => resendInvite(i)} title="Resend" data-testid={`resend-${i.id}`}><Send className="h-3.5 w-3.5" /></Button>
                              <Button size="sm" variant="outline" className="border-rose-300 text-rose-700" onClick={() => cancelInvite(i)} title="Cancel" data-testid={`cancel-${i.id}`}><XCircle className="h-3.5 w-3.5" /></Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invite dialog */}
      {inviteOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeInvite}>
          <Card className="w-full max-w-lg bg-white" onClick={(e) => e.stopPropagation()} data-testid="invite-dialog">
            <CardHeader><CardTitle className="text-base">Invite a new user</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {inviteResult ? (
                <div className="space-y-2">
                  <div className="p-3 rounded bg-emerald-50 border border-emerald-200 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-emerald-700 inline mr-1" /> Invitation sent.
                  </div>
                  <div className="text-sm space-y-1">
                    <div><strong>Reference:</strong> <code className="text-xs bg-slate-100 px-1.5 rounded">{inviteResult.invitation_ref}</code></div>
                    <div><strong>Status:</strong> {inviteResult.status}</div>
                    <div className="text-slate-500 text-xs">A styled invitation email + a confirmation to you have been queued.</div>
                  </div>
                  <div className="flex justify-end pt-2"><Button onClick={closeInvite} data-testid="invite-done">Done</Button></div>
                </div>
              ) : (
                <form onSubmit={submitInvite} className="space-y-3">
                  <div className="text-xs text-slate-600 p-2 bg-blue-50 border border-blue-200 rounded">
                    The user will receive a USKE-branded welcome email with a secure link to set their password (the same SMTP flow used for password resets). Their access will be scoped to the Division &amp; Department you pick below.
                  </div>
                  <div>
                    <Label>Full name *</Label>
                    <Input value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} required data-testid="invite-name" />
                  </div>
                  <div>
                    <Label>Work email *</Label>
                    <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} required data-testid="invite-email" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Role *</Label>
                      <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                        <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input value={inviteForm.phone} onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })} placeholder="(206) 555-0100" data-testid="invite-phone" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Division {inviteForm.role === 'division_admin' && '*'}</Label>
                      <Select value={inviteForm.division_id} onValueChange={(v) => setInviteForm({ ...inviteForm, division_id: v, department_id: '' })}>
                        <SelectTrigger data-testid="invite-division">
                          <SelectValue placeholder={inviteDivisions.length ? 'Select division' : 'No divisions yet'} />
                        </SelectTrigger>
                        <SelectContent>
                          {inviteDivisions.map(d => <SelectItem key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ''}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Department</Label>
                      <Select value={inviteForm.department_id} onValueChange={(v) => setInviteForm({ ...inviteForm, department_id: v })} disabled={!inviteForm.division_id}>
                        <SelectTrigger data-testid="invite-department">
                          <SelectValue placeholder={inviteForm.division_id ? (inviteDepartments.length ? 'Select department' : 'No departments in this division') : 'Pick a division first'} />
                        </SelectTrigger>
                        <SelectContent>
                          {inviteDepartments.map(d => <SelectItem key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ''}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={closeInvite}>Cancel</Button>
                    <Button type="submit" disabled={inviteSubmitting} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="invite-submit">
                      {inviteSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />} Send invitation
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reset dialog */}
      {resetTarget && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={closeReset}>
          <Card className="w-full max-w-md bg-white" onClick={(e) => e.stopPropagation()} data-testid="reset-password-dialog">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-base">Reset password for {resetTarget.name}</CardTitle>
              <Button size="sm" variant="ghost" onClick={closeReset}><X className="h-4 w-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {resetResult ? (
                <div className="space-y-2">
                  <div className="p-3 rounded bg-emerald-50 border border-emerald-200 text-sm">✓ Password reset. The user will be required to change it on next sign-in.</div>
                  <div className="text-sm space-y-1">
                    <div><strong>Sent to:</strong> {resetResult.user_email}</div>
                    <div className="break-all"><strong>Password:</strong> <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">{resetResult.used_password}</code> <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(resetResult.used_password); toast({ title: 'Copied' }); }}><ClipboardCopy className="h-3 w-3" /></Button></div>
                  </div>
                  <div className="flex justify-end pt-2"><Button onClick={closeReset} data-testid="reset-done">Done</Button></div>
                </div>
              ) : (
                <form onSubmit={submitReset} className="space-y-3">
                  <div className="text-xs text-slate-600 p-2 bg-blue-50 border border-blue-200 rounded">The user will be emailed the new password and forced to change it on next sign-in.</div>
                  <div>
                    <Label htmlFor="np">New password <span className="text-slate-400 text-[11px]">(blank to auto-generate)</span></Label>
                    <Input id="np" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} placeholder="leave blank for auto" data-testid="reset-new-password" />
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={closeReset}>Cancel</Button>
                    <Button type="submit" disabled={resetSubmitting} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="reset-submit">
                      {resetSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <KeyRound className="h-4 w-4 mr-1" />} Reset
                    </Button>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Audit modal */}
      {auditFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setAuditFor(null)}>
          <Card className="w-full max-w-2xl bg-white max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="user-audit-dialog">
            <CardHeader className="border-b pb-3">
              <CardTitle className="text-base flex items-center gap-2"><ScrollText className="h-4 w-4 text-blue-700" /> Audit trail · {auditFor.name}</CardTitle>
              <p className="text-xs text-slate-500">{auditFor.email}</p>
            </CardHeader>
            <CardContent className="overflow-y-auto p-0">
              {auditRows.length === 0 ? <div className="text-center py-8 text-slate-400 text-sm">No audit entries.</div> : (
                <div className="divide-y">
                  {auditRows.map((e, idx) => (
                    <div key={idx} className="p-3 flex items-start gap-3 hover:bg-slate-50">
                      <ChevronRight className="h-4 w-4 text-slate-400 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-mono font-semibold text-slate-800">{e.event}</span>
                          <span className="text-[11px] text-slate-500">by <strong>{e.actor}</strong></span>
                          {e.ip && <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-mono">{e.ip}</span>}
                          <span className="text-[10px] text-slate-400 ml-auto">{new Date(e.at).toLocaleString()}</span>
                        </div>
                        {e.meta && Object.keys(e.meta).length > 0 && (
                          <pre className="text-[10px] bg-slate-50 border border-slate-100 rounded p-2 mt-1 overflow-x-auto">{JSON.stringify(e.meta, null, 2)}</pre>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
            <div className="border-t p-3 flex justify-end"><Button variant="outline" onClick={() => setAuditFor(null)}>Close</Button></div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default GovAgencyUsers;
